import { State, Selector, Action, StateContext, Store, Select } from '@ngxs/store';
import { GetRemindersToday, GetAllReminders, CreateReminder, UpdateReminder, DeleteReminder, ClearReminderAlerts, CreateReminderAlert, DismissReminderAlert } from './reminder.actions';
import { ReminderService } from 'app/services/reminder/reminder.service';
import { tap, catchError } from 'rxjs/operators';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { Reminder } from 'app/models/dto/reminder.model';
import { Observable, of } from 'rxjs';
import { CreateNewBorrowerAlert } from '../borrower/alerts/borrower-alerts.actions';
import { Alert } from 'app/models/dto/alert.model';
import { Injectable } from '@angular/core';
import { LenderUserState } from '../lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Location } from '@angular/common';
import { format } from 'date-fns';
export class ReminderStateModel {
  allReminders: Reminder[];
  remindersToday: Reminder[];
  allRemindersLoaded: boolean;
  alerts: Alert[];
}

@State<ReminderStateModel>({
  name: 'reminder',
  defaults: {
    allReminders: [],
    remindersToday: [],
    allRemindersLoaded: false,
    alerts: []
  }
})

@Injectable()

export class ReminderState {
  @Selector()
  static allReminders(state: ReminderStateModel) {
    return state.allReminders;
  }

  @Selector()
  static remindersToday(state: ReminderStateModel) {
    return state.remindersToday;
  }

  @Selector()
  static allRemindersLoaded(state: ReminderStateModel) {
    return state.allRemindersLoaded;
  }

  @Selector()
  static alerts(state: ReminderStateModel) {
    return state.alerts;
  }

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  private remindersTodayPusherChannel: any = null;

  constructor(
    private reminderService: ReminderService,
    private pusherService: PusherService,
    private store: Store,
    private location: Location
  ) { }

  @Action(GetRemindersToday)
  getRemindersToday({ patchState }: StateContext<ReminderStateModel>, _: GetRemindersToday) {
    this.pusherSubscribeRemindersToday();
    return this.reminderService.remindersToday()
      .pipe(tap(remindersToday => {
        patchState({ remindersToday });
      }));
  }

  @Action(GetAllReminders)
  getAllReminders({ patchState }: StateContext<ReminderStateModel>, _: GetAllReminders) {
    return this.reminderService.allReminders()
      .pipe(tap(allReminders => {
        patchState({ allReminders, allRemindersLoaded: true });
      }));
  }

  @Action(UpdateReminder)
  updateReminder({ getState, patchState }: StateContext<ReminderStateModel>, { reminderId, updates }: UpdateReminder) {
    return this.reminderService.update(reminderId, updates).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error updating this reminder. Please try again.',
        dismissDelay: 5000
      };
      patchState({ alerts: [...getState().alerts, newAlert] });
      return of();
    }),
      tap(updatedReminderResponse => {
        const remindersCopy = [...getState().allReminders];
        const oldReminderIndex = remindersCopy.findIndex(r => r.id === reminderId);
        remindersCopy[oldReminderIndex] = updatedReminderResponse;
        const newAlert: Alert = {
          level: 'success',
          message: 'Reminder successfully updated.',
          dismissDelay: 5000
        };
        patchState({
          alerts: [...getState().alerts, newAlert],
          allReminders: remindersCopy
        });
      }));
  }

  @Action(DeleteReminder)
  deleteReminder({ getState, patchState }: StateContext<ReminderStateModel>, { reminderId }: DeleteReminder) {
    const path = this.location.path();
    return this.reminderService.delete(reminderId).pipe(
      catchError(() => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error canceling this reminder. Please try again.',
          dismissDelay: 5000
        };
        if (path.includes('/borrower')) {
          this.store.dispatch(new CreateNewBorrowerAlert(newAlert));
        }
        patchState({ alerts: [...getState().alerts, newAlert] });
        return of();
      }),
      tap(() => {
        const newAlert: Alert = {
          level: 'success',
          message: 'Reminder has been canceled.',
          dismissDelay: 5000
        };
        patchState({
          alerts: [...getState().alerts, newAlert],
          allReminders: [...getState().allReminders.filter(r => r.id !== reminderId)]
        });
      }));
  }

  @Action(CreateReminder)
  createReminder({ getState, patchState }: StateContext<ReminderStateModel>, { newReminder }: CreateReminder) {
    return this.reminderService.create(newReminder).pipe(
      catchError(() => {
        const newAlert: Alert = {
          level: 'error',
          message: 'There was an error scheduling a reminder for this borrower. Please try again.',
          dismissDelay: 5000
        };
        patchState({ alerts: [...getState().alerts, newAlert] });
        this.store.dispatch(new CreateNewBorrowerAlert(newAlert));
        return of();
      }),
      tap(newReminderResponse => {
        const newAlert: Alert = {
          level: 'success',
          message: `Reminder set for ${format(new Date(newReminderResponse.expires), 'PP p')}.`,
          dismissDelay: 5000
        };
        patchState({
          alerts: [...getState().alerts, newAlert],
          allReminders: [...getState().allReminders, newReminderResponse]
        });
        this.store.dispatch(new CreateNewBorrowerAlert(newAlert));
      }));
  }

  @Action(CreateReminderAlert)
  createReminderAlert({ getState, patchState }: StateContext<ReminderStateModel>, { alert }: CreateReminderAlert) {
    patchState({ alerts: [...getState().alerts, alert] });
  }

  @Action(DismissReminderAlert)
  dismissReminderAlert({ getState, patchState }: StateContext<ReminderStateModel>, { alert }: DismissReminderAlert) {
    patchState({ alerts: [...getState().alerts.filter(a => a && a.message && a.message !== alert.message)] });
  }

  @Action(ClearReminderAlerts)
  clearReminderAlerts({ patchState }: StateContext<ReminderStateModel>, { }: ClearReminderAlerts) {
    patchState({ alerts: [] });
  }

  private pusherSubscribeRemindersToday() {
    this.lenderUser$.subscribe(user => {
      if (this.remindersTodayPusherChannel) {
        // avoid subscribing more than once
        return;
      }

      this.remindersTodayPusherChannel = this.pusherService.subscribe(`lender-user-${user.id}-reminders-today`);
      this.remindersTodayPusherChannel.bind_global((eventName: string) => {
        switch (eventName) {
          case 'reminder-updated':
          case 'reminder-deleted':
          case 'reminder-created':
            this.store.dispatch(new GetRemindersToday());
            break;
        }
      });
    });
  }
}
